import type {
  ForgotPasswordForm,
  ForgotPasswordResponse,
} from '@gokrew/models/auth';
import { Form } from 'formik';
import { FormField, FormikWithSchema } from '@gokrew/ui/form';
import { Button } from '@gokrew/ui/components/Button';
import { AuthLayout } from '@gokrew/ui/layouts/AuthLayout';
import { KollabHRLogo } from '@gokrew/ui/assets';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { forgotPasswordSchema } from '@gokrew/ui/validation/authSchema';
import { useMutation } from '@tanstack/react-query';
import { sendForgotPasswordLink } from '@/api/auth';
import toast from 'react-hot-toast';

const initialValues: ForgotPasswordForm = {
  email: '',
};

export function ForgotPasswordPage() {
  const { t } = useTranslation(TranslationNS_Admin.Auth);

  const { mutate, isPending } = useMutation<
    ForgotPasswordResponse,
    Error,
    string
  >({
    mutationFn: (email) => sendForgotPasswordLink(email),
    onSuccess: () => {
      toast.success(t('forgot_password.toast.success'));
    },
    onError: () => {
      toast.error(t('forgot_password.toast.error'));
    },
  });

  return (
    <AuthLayout>
      <div className="bg-[#F9F5FF] rounded-2xl m-4 p-8 sm:p-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center w-full">
            <img className="w-20 mb-4" src={KollabHRLogo} alt="Logo" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('forgot_password.forgot_password')}
          </h2>
        </div>

        <FormikWithSchema
          initialValues={initialValues}
          onSubmit={(values) => {
            mutate(values.email);
          }}
          validationSchema={forgotPasswordSchema}
        >
          {({ isValid, dirty }) => (
            <Form>
              <FormField
                name="email"
                type="email"
                label={t('form.email_label')}
                placeholder={t('form.email_placeholder')}
              />

              <Button
                type="submit"
                disabled={!isValid || !dirty || isPending}
                className="w-full mt-1.5 mb-3"
              >
                {isPending ? '...' : t('forgot_password.send_reset_link')}
              </Button>

              <p className="text-center text-gray-600 pt-2">
                {t('forgot_password.remember_password')}{' '}
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t('login_page.login')}
                </Link>
              </p>
            </Form>
          )}
        </FormikWithSchema>
      </div>
    </AuthLayout>
  );
}
